import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils';

/**
 * Status pills.
 *
 * The `tone` variants are the console's own vocabulary rather than shadcn's:
 * an operations screen has to say "queued / running / failed" in six
 * distinguishable colours, and each tone is a (background, foreground)
 * couple so contrast holds by construction instead of by whoever picked the
 * classes. Written out in full because Tailwind only sees classes that
 * appear literally in source.
 */
const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1.5 overflow-hidden whitespace-nowrap rounded-md border font-medium [&>svg]:size-3 [&>svg]:pointer-events-none',
  {
    variants: {
      /**
       * `sm` is the chip that sits inside a table row, where a 22px pill
       * pushes the row height past the design's 38px and the table stops
       * fitting a screenful.
       */
      size: {
        default: 'h-[22px] px-2 text-xs',
        sm: 'h-5 rounded-[10px] px-1.5 text-[11.5px]',
      },
      // Kept in step with TONES in components/table/tone.ts — same names,
      // same pairs, so a badge and a status dot cannot disagree.
      tone: {
        ok: 'border-transparent bg-ok-bg text-ok-fg',
        warn: 'border-transparent bg-warn-bg text-warn-fg',
        err: 'border-transparent bg-err-bg text-err-fg',
        info: 'border-transparent bg-info-bg text-info-fg',
        mute: 'border-transparent bg-mute-bg text-mute-fg',
        accent: 'border-transparent bg-primary/10 text-primary',
        purple: 'border-transparent bg-purple-bg text-purple-fg',
        outline: 'border-border bg-card text-foreground',
      },
    },
    defaultVariants: { tone: 'mute', size: 'default' },
  },
);

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>['tone']>;

export type BadgeProps = ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { readonly asChild?: boolean };

export function Badge({ className, tone, size, asChild = false, ...props }: BadgeProps) {
  const Comp = asChild ? Slot.Root : 'span';

  return <Comp data-slot="badge" className={cn(badgeVariants({ tone, size }), className)} {...props} />;
}

export { badgeVariants };
